<div class="container-fluid complyon-page policies-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell policies-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title policies-title mb-2 text-l">Policies</h1>
      <p class="complyon-subtitle policies-subtitle mb-0 text-s">Policy versions, acknowledgements, exceptions and workflow rules.</p>
    </section>

    <div class="d-flex align-items-start justify-content-between gap-3 mb-3 policies-toolbar">
      <div class="d-inline-flex flex-wrap align-items-center gap-1 policies-tab-bar rounded-4 p-1" role="tablist" aria-label="Policies tabs">
        <button
          v-for="tab in policy_tabs"
          :key="tab.id"
          type="button"
          class="btn policies-tab-btn d-inline-flex align-items-center gap-2"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="policies-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn btn-link text-decoration-none policies-reset-btn d-inline-flex align-items-center gap-2">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </div>

    <div v-if="active_tab === 'register'">
      <section class="complyon-panel policies-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="policies-section-title text-s">Policy register</div>
            <div class="policies-section-subtitle text-xs">Every policy with owner, version, approval status, acknowledgement and review date.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="open_policy_modal()">
            <i class="bi bi-plus"></i>
            <span>New policy</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table policies-table policies-approval-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Policy</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">Ver.</th>
                <th class="text-xs">Status</th>
                <th class="text-xs">Acknowledged</th>
                <th class="text-xs">Controls</th>
                <th class="text-xs">Review</th>
                <th class="text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in register_rows"
                :key="row.id"
                class="policies-row-clickable"
                role="button"
                tabindex="0"
                @click="open_policy_drawer(row)"
                @keydown.enter.prevent="open_policy_drawer(row)"
                @keydown.space.prevent="open_policy_drawer(row)"
              >
                <td class="ps-3 fw-semibold">{{ row.policy }}</td>
                <td>{{ row.category }}</td>
                <td class="fw-semibold">{{ row.version }}</td>
                <td><span class="badge rounded-pill policies-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
                <td>
                  <div v-if="row.acknowledged !== null" class="d-flex align-items-center gap-3">
                    <div class="policies-progress flex-grow-1">
                      <div class="progress rounded-pill">
                        <div class="progress-bar rounded-pill" :style="{ width: `${row.acknowledged}%`, backgroundColor: row.ack_tone }"></div>
                      </div>
                    </div>
                    <div class="policies-progress-value">{{ row.acknowledged }}%</div>
                  </div>
                  <span v-else class="text-secondary">Not issued</span>
                </td>
                <td>{{ row.controls }}</td>
                <td>
                  <span class="badge rounded-pill policies-pill text-xs" :class="row.review_class">{{ row.review }}</span>
                </td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right policies-row-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'approvals'">
      <section class="row g-3 mb-3">
        <div v-for="card in approval_stats" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card policies-card rounded-4 p-3 h-100">
            <div class="policies-stat-label text-xs">{{ card.label }}</div>
            <div class="policies-stat-value" :class="`is-${card.tone}`">{{ card.value }}</div>
            <div class="policies-stat-note text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel policies-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="policies-section-title text-s">Version control &amp; approval workflow</div>
            <div class="policies-section-subtitle text-xs">Owner - legal &amp; compliance - executive - board. Each policy's progress through the approval route.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table policies-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Policy</th>
                <th class="text-xs">Ver.</th>
                <th class="text-xs">Status</th>
                <th class="text-xs">Approval workflow</th>
                <th class="text-xs">Approver</th>
                <th class="text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in approval_rows"
                :key="row.policy"
                class="policies-row-clickable"
                role="button"
                tabindex="0"
                :class="{ 'policies-row-highlight': row.highlight }"
                @click="open_policy_drawer_by_policy(row.policy)"
                @keydown.enter.prevent="open_policy_drawer_by_policy(row.policy)"
                @keydown.space.prevent="open_policy_drawer_by_policy(row.policy)"
              >
                <td class="ps-3 fw-semibold">{{ row.policy }}</td>
                <td class="fw-semibold">{{ row.version }}</td>
                <td><span class="badge rounded-pill policies-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
                <td>
                  <div class="policies-workflow">
                    <span
                      v-for="(step, index) in row.workflow"
                      :key="`${row.policy}-${index}`"
                      class="policies-workflow-step-wrap"
                    >
                      <span
                        class="policies-workflow-step"
                        :class="`is-${step.state}`"
                      >
                        <i v-if="step.state === 'done'" class="bi bi-check-lg"></i>
                        <span v-else>{{ step.label }}</span>
                      </span>
                      <span
                        v-if="index < row.workflow.length - 1"
                        class="policies-workflow-connector"
                      ></span>
                    </span>
                  </div>
                </td>
                <td>{{ row.approver }}</td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right policies-row-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'acknowledgement'">
      <section class="row g-3 mb-3">
        <div v-for="card in acknowledgement_stats" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card policies-card rounded-4 p-3 h-100">
            <div class="policies-stat-label text-xs">{{ card.label }}</div>
            <div class="policies-stat-value" :class="`is-${card.tone}`">{{ card.value }}</div>
            <div class="policies-stat-note text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel policies-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="policies-section-title text-s">Adoption - staff acknowledgement</div>
            <div class="policies-section-subtitle text-xs">Proof that staff have read and accepted each policy. Open a policy to record acceptances.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table policies-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Policy</th>
                <th class="text-xs">Acknowledged</th>
                <th class="text-xs">Accepted</th>
                <th class="text-xs">Outstanding</th>
                <th class="text-xs">Status</th>
                <th class="text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in acknowledgement_rows"
                :key="row.policy"
                class="policies-row-clickable"
                role="button"
                tabindex="0"
                @click="open_policy_drawer_by_policy(row.policy)"
                @keydown.enter.prevent="open_policy_drawer_by_policy(row.policy)"
                @keydown.space.prevent="open_policy_drawer_by_policy(row.policy)"
              >
                <td class="ps-3 fw-semibold">{{ row.policy }}</td>
                <td>
                  <div class="d-flex align-items-center gap-3">
                    <div class="policies-progress flex-grow-1">
                      <div class="progress rounded-pill">
                        <div class="progress-bar rounded-pill" :style="{ width: `${row.bar}%`, backgroundColor: row.tone }"></div>
                      </div>
                    </div>
                    <div class="policies-progress-value">{{ row.percent }}</div>
                  </div>
                </td>
                <td class="fw-semibold">{{ row.accepted }}</td>
                <td class="fw-semibold" :class="row.outstanding ? 'text-danger' : ''">{{ row.outstanding }}</td>
                <td><span class="badge rounded-pill policies-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right policies-row-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'exceptions'">
      <section class="complyon-panel policies-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="policies-section-title text-s">Policy exceptions</div>
            <div class="policies-section-subtitle text-xs">Approved, time-bound deviations from policy - who requested, who approved, and when they expire.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="open_exception_modal()">
            <i class="bi bi-plus"></i>
            <span>Log exception</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table policies-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Exception</th>
                <th class="text-xs">Against policy</th>
                <th class="text-xs">Requested by</th>
                <th class="text-xs">Approved by</th>
                <th class="text-xs">Expires</th>
                <th class="text-xs">Status</th>
                <th class="text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in exception_rows"
                :key="row.id"
                class="policies-row-clickable"
                role="button"
                tabindex="0"
                @click="open_exception_modal(row)"
                @keydown.enter.prevent="open_exception_modal(row)"
                @keydown.space.prevent="open_exception_modal(row)"
              >
                <td class="ps-3 fw-semibold">{{ row.exception }}</td>
                <td>{{ row.policy }}</td>
                <td>{{ row.requested_by }}</td>
                <td>{{ row.approved_by }}</td>
                <td class="fw-semibold">{{ row.expires }}</td>
                <td><span class="badge rounded-pill policies-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right policies-row-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'categories'">
      <section class="complyon-panel policies-panel rounded-4 overflow-hidden">
        <div class="px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div class="policies-section-title text-s">Policy categories</div>
          <div class="policies-section-subtitle text-xs">The policy framework by domain. Click a category to filter the register.</div>
        </div>
        <div class="px-3 px-md-4 pb-3 pb-md-4">
          <div class="row g-3">
            <div v-for="card in category_cards_view" :key="card.title" class="col-12 col-md-6 col-xl-4">
              <article
                class="policies-category-card rounded-4 p-3 h-100"
                :class="{ 'is-active': active_category_filter === card.title }"
                role="button"
                tabindex="0"
                @click="set_category_filter(card.title)"
                @keydown.enter.prevent="set_category_filter(card.title)"
                @keydown.space.prevent="set_category_filter(card.title)"
              >
                <span class="policies-category-icon d-inline-flex align-items-center justify-content-center">
                  <i class="bi" :class="card.icon"></i>
                </span>
                <div class="policies-category-title mt-3">{{ card.title }}</div>
                <div class="policies-category-copy">{{ card.subtitle }}</div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section class="complyon-panel policies-panel rounded-4 overflow-hidden mt-3">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="policies-section-title text-s">{{ active_category_filter }} policies</div>
            <div class="policies-section-subtitle text-xs">Showing policies registered under this category. Select another card above to switch the list.</div>
          </div>
          <div class="badge rounded-pill policies-pill text-xs bg-success-subtle text-success-emphasis align-self-start">
            {{ filtered_category_rows.length }} item<span v-if="filtered_category_rows.length !== 1">s</span>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table policies-table policies-approval-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Policy</th>
                <th class="text-xs">Ver.</th>
                <th class="text-xs">Status</th>
                <th class="text-xs">Acknowledged</th>
                <th class="text-xs">Controls</th>
                <th class="text-xs">Review</th>
                <th class="text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filtered_category_rows"
                :key="row.id"
                class="policies-row-clickable"
                role="button"
                tabindex="0"
                @click="open_policy_drawer(row)"
                @keydown.enter.prevent="open_policy_drawer(row)"
                @keydown.space.prevent="open_policy_drawer(row)"
              >
                <td class="ps-3 fw-semibold">{{ row.policy }}</td>
                <td class="fw-semibold">{{ row.version }}</td>
                <td><span class="badge rounded-pill policies-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
                <td>
                  <div v-if="row.acknowledged !== null" class="d-flex align-items-center gap-3">
                    <div class="policies-progress flex-grow-1">
                      <div class="progress rounded-pill">
                        <div class="progress-bar rounded-pill" :style="{ width: `${row.acknowledged}%`, backgroundColor: row.ack_tone }"></div>
                      </div>
                    </div>
                    <div class="policies-progress-value">{{ row.acknowledged }}%</div>
                  </div>
                  <span v-else class="text-secondary">Not issued</span>
                </td>
                <td>{{ row.controls }}</td>
                <td>
                  <span class="badge rounded-pill policies-pill text-xs" :class="row.review_class">{{ row.review }}</span>
                </td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right policies-row-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'reviews'">
      <section class="complyon-panel policies-panel rounded-4 overflow-hidden mb-3">
        <div class="policies-review-banner px-3 px-md-4 py-3 d-flex align-items-center gap-3">
          <span class="policies-review-banner-icon d-inline-flex align-items-center justify-content-center"><i class="bi bi-calendar3"></i></span>
          <div>
            <div class="policies-review-banner-title">Review reminders</div>
            <div class="policies-review-banner-copy">1 policy review(s) overdue. Complyon alerts the owner 30 days before each review date.</div>
          </div>
        </div>
      </section>

      <section class="complyon-panel policies-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="policies-section-title text-s">Policy review schedule</div>
            <div class="policies-section-subtitle text-xs">Automatic alerts before policies expire or require review.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table policies-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Policy</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Review date</th>
                <th class="text-xs">Reminder</th>
                <th class="text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in review_rows"
                :key="row.policy"
                class="policies-row-clickable"
                role="button"
                tabindex="0"
                @click="open_policy_drawer_by_policy(row.policy)"
                @keydown.enter.prevent="open_policy_drawer_by_policy(row.policy)"
                @keydown.space.prevent="open_policy_drawer_by_policy(row.policy)"
              >
                <td class="ps-3 fw-semibold">
                  <span class="policies-row-icon" :class="row.icon_class">
                    <i class="bi" :class="row.icon"></i>
                  </span>
                  <span>{{ row.policy }}</span>
                </td>
                <td>{{ row.owner }}</td>
                <td class="fw-semibold">{{ row.date }}</td>
                <td><span class="badge rounded-pill policies-pill text-xs" :class="row.reminder_class">{{ row.reminder }}</span></td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right policies-row-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>

  <div
    v-if="policy_modal_open"
    class="policies-modal-backdrop"
    @click.self="close_policy_modal"
  >
    <section class="policies-modal-shell" role="dialog" aria-modal="true" :aria-label="policy_modal_mode === 'edit' ? 'Edit policy' : 'New policy'">
      <header class="policies-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="policies-modal-title">{{ policy_modal_mode === 'edit' ? 'Edit policy' : 'New policy' }}</div>
          <div class="policies-modal-subtitle">Core policy record. Acknowledgement, controls and training are managed in the policy detail.</div>
        </div>
        <button type="button" class="btn btn-link p-0 policies-modal-close" @click="close_policy_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="policies-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="policies-modal-label">Policy name</label>
            <input v-model="policy_modal_form.policy" type="text" class="form-control policies-modal-input" placeholder="e.g. Anti-bribery &amp; corruption policy">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Category</label>
            <select v-model="policy_modal_form.category" class="form-select policies-modal-select">
              <option v-for="option in policy_category_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Version</label>
            <input v-model="policy_modal_form.version" type="text" class="form-control policies-modal-input" placeholder="e.g. v1">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Policy owner</label>
            <input v-model="policy_modal_form.owner" type="text" class="form-control policies-modal-input" placeholder="Role - name">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Approver</label>
            <input v-model="policy_modal_form.approver" type="text" class="form-control policies-modal-input" placeholder="e.g. Board / EXCO / CFO">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Status</label>
            <select v-model="policy_modal_form.status" class="form-select policies-modal-select">
              <option v-for="option in policy_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Approval stage</label>
            <select v-model="policy_modal_form.approval_stage" class="form-select policies-modal-select">
              <option v-for="option in policy_approval_stage_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Board approval required</label>
            <select v-model="policy_modal_form.board_approval_required" class="form-select policies-modal-select">
              <option v-for="option in policy_board_approval_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Review status</label>
            <select v-model="policy_modal_form.review_status" class="form-select policies-modal-select">
              <option v-for="option in policy_review_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Next review date</label>
            <input v-model="policy_modal_form.next_review_date" type="text" class="form-control policies-modal-input" placeholder="e.g. Mar 2027">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Effective date</label>
            <input v-model="policy_modal_form.effective_date" type="text" class="form-control policies-modal-input" placeholder="e.g. 01 Apr 2024">
          </div>

          <div class="col-12">
            <label class="policies-modal-label">Summary</label>
            <textarea v-model="policy_modal_form.summary" class="form-control policies-modal-textarea" rows="4" placeholder="What this policy governs."></textarea>
          </div>
        </div>
      </div>

      <footer class="policies-modal-footer d-flex align-items-center justify-content-end gap-2 border-top">
        <button type="button" class="btn btn-link policies-modal-cancel" @click="close_policy_modal">Cancel</button>
        <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="save_policy_modal">
          <i class="bi bi-plus-lg" v-if="policy_modal_mode !== 'edit'"></i>
          <i class="bi bi-check2" v-else></i>
          <span>{{ policy_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
        </button>
      </footer>
    </section>
  </div>

  <div
    v-if="exception_modal_open"
    class="policies-modal-backdrop"
    @click.self="close_exception_modal"
  >
    <section class="policies-modal-shell" role="dialog" aria-modal="true" :aria-label="exception_modal_mode === 'edit' ? 'Edit policy exception' : 'New policy exception'">
      <header class="policies-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="policies-modal-title">{{ exception_modal_mode === 'edit' ? 'Edit policy exception' : 'New policy exception' }}</div>
          <div class="policies-modal-subtitle">An approved, time-bound deviation from a policy.</div>
        </div>
        <button type="button" class="btn btn-link p-0 policies-modal-close" @click="close_exception_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="policies-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="policies-modal-label">Exception</label>
            <input v-model="exception_modal_form.exception" type="text" class="form-control policies-modal-input" placeholder="e.g. Emergency single-source procurement">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Against policy</label>
            <select v-model="exception_modal_form.policy" class="form-select policies-modal-select">
              <option v-for="row in register_rows" :key="row.id" :value="row.policy">{{ row.policy }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Requested by</label>
            <input v-model="exception_modal_form.requested_by" type="text" class="form-control policies-modal-input" placeholder="Team / person">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Approved by</label>
            <input v-model="exception_modal_form.approved_by" type="text" class="form-control policies-modal-input" placeholder="Approver">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Expires</label>
            <input v-model="exception_modal_form.expires" type="text" class="form-control policies-modal-input" placeholder="e.g. 30 Jun 2026">
          </div>

          <div class="col-12 col-md-6">
            <label class="policies-modal-label">Status</label>
            <select v-model="exception_modal_form.status" class="form-select policies-modal-select">
              <option v-for="option in exception_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>

      <footer class="policies-modal-footer d-flex align-items-center justify-content-between gap-2 border-top">
        <button
          v-if="exception_modal_mode === 'edit'"
          type="button"
          class="btn btn-link text-danger p-0"
          @click="delete_exception_modal"
        >
          <i class="bi bi-trash me-1"></i>
          <span>Delete</span>
        </button>
        <span v-else></span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-link policies-modal-cancel" @click="close_exception_modal">Cancel</button>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="save_exception_modal">
            <i class="bi bi-plus-lg" v-if="exception_modal_mode !== 'edit'"></i>
            <i class="bi bi-check2" v-else></i>
            <span>{{ exception_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
          </button>
        </div>
      </footer>
    </section>
  </div>

  <div
    v-if="policy_drawer_open && active_policy_drawer"
    class="command-centre-drawer-backdrop"
    @click.self="close_policy_drawer"
  ></div>
  <aside
    v-if="policy_drawer_open && active_policy_drawer"
    class="command-centre-drawer command-centre-drawer--policies"
    role="dialog"
    aria-modal="true"
  >
    <header class="command-centre-drawer-header policies-drawer-header border-bottom">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div class="policies-drawer-kicker">{{ active_policy_drawer.section }}</div>
          <div class="policies-drawer-title mt-1">{{ active_policy_drawer.title }}</div>
          <div class="policies-drawer-subtitle mt-1">{{ active_policy_drawer.subtitle }}</div>
        </div>
        <button type="button" class="btn btn-link p-0 command-centre-drawer-close" @click="close_policy_drawer" aria-label="Close drawer">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="d-flex align-items-center justify-content-end mt-2">
        <span class="badge rounded-pill policies-pill" :class="active_policy_drawer.status_class">{{ active_policy_drawer.status }}</span>
      </div>
    </header>

    <div class="command-centre-drawer-body policies-drawer-body">
      <p class="policies-drawer-description mb-3">{{ active_policy_drawer.description }}</p>

      <section class="mb-4">
        <div class="policies-drawer-section-title">{{ active_policy_drawer.approval_workflow_title || 'APPROVAL WORKFLOW' }}</div>
        <div class="policies-drawer-workflow mt-2">
          <span
            v-for="(step, index) in active_policy_drawer.approval_workflow"
            :key="`${active_policy_drawer.title}-step-${index}`"
            class="policies-drawer-workflow-step-wrap"
          >
            <span class="policies-drawer-workflow-step" :class="`is-${step.state}`">
              <i v-if="step.state === 'done'" class="bi bi-check-lg"></i>
              <span v-else>{{ step.label_value || (index + 1) }}</span>
            </span>
            <span v-if="index < active_policy_drawer.approval_workflow.length - 1" class="policies-drawer-workflow-connector"></span>
          </span>
        </div>
        <div class="policies-drawer-note mt-2">{{ active_policy_drawer.approval_note }}</div>
      </section>

      <section class="mb-4">
        <div class="d-flex align-items-center justify-content-between gap-2">
          <div class="policies-drawer-section-title">{{ active_policy_drawer.adoption.label }}</div>
          <span v-if="active_policy_drawer.adoption.percent !== null" class="policies-drawer-ai-pill">AI</span>
        </div>
        <div v-if="active_policy_drawer.adoption.percent !== null" class="mt-2">
          <div class="progress rounded-pill policies-drawer-progress">
            <div class="progress-bar rounded-pill" :style="{ width: `${active_policy_drawer.adoption.percent}%` }"></div>
          </div>
          <div class="d-flex align-items-center justify-content-between mt-1 policies-drawer-metric">
            <span>{{ active_policy_drawer.adoption.outstanding_label }}</span>
            <span>{{ active_policy_drawer.adoption.percent }}% - {{ active_policy_drawer.adoption.accepted }}</span>
          </div>
          <div class="d-flex flex-wrap gap-2 mt-2">
            <span v-for="name in active_policy_drawer.adoption.names" :key="name" class="badge rounded-pill policies-chip">{{ name }}</span>
          </div>
        </div>
        <div v-else class="policies-drawer-note mt-2">{{ active_policy_drawer.adoption.outstanding_label }}</div>
      </section>

      <section class="mb-4">
        <div class="policies-drawer-section-title">{{ active_policy_drawer.obligations.title }}</div>
        <div v-if="active_policy_drawer.obligations.items.length" class="d-grid gap-2 mt-2">
          <article v-for="item in active_policy_drawer.obligations.items" :key="item.title" class="policies-drawer-card">
            <div class="policies-drawer-card-title">{{ item.title }}</div>
            <div class="policies-drawer-card-subtitle">{{ item.subtitle }}</div>
          </article>
        </div>
        <div v-else class="policies-drawer-note mt-2">{{ active_policy_drawer.obligations.empty_note || 'No obligation mapped - link the law, regulation or contract clause this policy satisfies.' }}</div>
      </section>

      <section class="mb-4">
        <div class="policies-drawer-section-title">{{ active_policy_drawer.enforcement.title }}</div>
        <div v-if="active_policy_drawer.enforcement.items.length" class="d-grid gap-2 mt-2">
          <article v-for="item in active_policy_drawer.enforcement.items" :key="item.title" class="policies-drawer-card">
            <div class="policies-drawer-card-title">{{ item.title }}</div>
            <div class="policies-drawer-card-subtitle">{{ item.subtitle }}</div>
          </article>
        </div>
        <div v-else class="policies-drawer-note mt-2">{{ active_policy_drawer.enforcement.empty_note || 'No controls linked yet - policy is not enforced by an automated check.' }}</div>
      </section>

      <section class="mb-4">
        <div class="policies-drawer-section-title">LINKED TRAINING</div>
        <div v-if="active_policy_drawer.training.length" class="d-flex flex-wrap gap-2 mt-2">
          <span v-for="item in active_policy_drawer.training" :key="item" class="badge rounded-pill policies-pill policies-training-pill">{{ item }}</span>
        </div>
        <div v-else class="policies-drawer-note mt-2">No mandatory training attached.</div>
      </section>

      <section>
        <div class="policies-drawer-section-title">LIFECYCLE</div>
        <div class="row g-2 mt-1">
          <div class="col-6">
            <div class="policies-drawer-meta-label">Version</div>
            <div class="policies-drawer-meta-value">{{ active_policy_drawer.lifecycle.version }}</div>
          </div>
          <div class="col-6">
            <div class="policies-drawer-meta-label">Effective</div>
            <div class="policies-drawer-meta-value">{{ active_policy_drawer.lifecycle.effective }}</div>
          </div>
          <div class="col-6">
            <div class="policies-drawer-meta-label">Next review</div>
            <div class="policies-drawer-meta-value">{{ active_policy_drawer.lifecycle.next_review }}</div>
          </div>
        </div>
      </section>
    </div>

    <footer class="command-centre-drawer-footer policies-drawer-footer">
      <div class="d-flex align-items-center justify-content-between gap-3">
        <button type="button" class="btn btn-link text-decoration-none policies-drawer-footer-link" @click="close_policy_drawer">Close</button>
        <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="open_policy_modal(active_policy)">
          <i class="bi bi-gear"></i>
          <span>{{ active_policy_drawer.footer.edit_label }}</span>
        </button>
      </div>
    </footer>
  </aside>
</div>
